import { Component, Vue } from 'vue-property-decorator';
import { fetchFeedbacks, updateFeedbackStatus } from '@/services';
import { FEEDBACK_STATUSES, FEEDBACK_TYPES, NOTIFICATION } from '@/constants';
import { DisplayStatus, FeedbackDTO, SelectionOptions, TableHeaderColumns } from '@/models';
import Avatar from '@/components/common/avatar/Avatar.vue';
import FilterDropdown from '@/components/common/filter-dropdown/FilterDropdown.vue';
import Status from '@/components/common/status/Status.vue';
import SearchInput from '@/components/common/search-input/SearchInput.vue';
import Pagination from '@/components/common/pagination/Pagination.vue';
import FeedbackRejectForm from './feedback-reject-form/FeedbackRejectForm.vue';

@Component({
  components: {
    Avatar,
    FilterDropdown,
    SearchInput,
    Status,
    Pagination,
    FeedbackRejectForm
  }
})
export default class FeedbackManagement extends Vue {
  private imageUrl = process.env.VUE_APP_HIVE_HOME_URL + '/images/';
  private feedbacks: FeedbackDTO[] = [];
  private selectedFeedback = {};
  private search = '';
  private selectedStatuss = '';
  private selectedTypes = '';
  private selectedImage = '';
  private page = 1;
  private pageCount = 0;
  private itemsPerPage = 5;
  private dialogRejectShown = false;
  private dialogImageShown = false;

  get tableHeaderColumns(): TableHeaderColumns {
    return [
      { text: 'ID', value: 'id', class: 'grey--text', align: 'start', width: '3%' },
      { text: 'Reporter', value: 'user', class: 'grey--text', align: 'start', width: '17%' },
      {
        text: 'Type',
        value: 'type',
        class: 'grey--text',
        align: 'start',
        width: '15%',
        filter: (value: string) => {
          return this.selectedTypes.length === 0 || this.selectedTypes.includes(value);
        }
      },
      { text: 'Description', value: 'description', class: 'grey--text', align: 'start', width: '20%' },
      { text: 'Image', value: 'image', class: 'grey--text', align: 'center', width: '15%' },
      {
        text: 'Status',
        value: 'status',
        class: 'grey--text',
        align: 'start',
        width: '7%',
        filter: (value: string) => {
          return this.selectedStatuss.length === 0 || this.selectedStatuss.includes(value);
        }
      },
      { text: 'Reject Reason', value: 'rejectReason', class: 'grey--text', align: 'start', width: '20%' },
      { text: 'Actions', value: 'actions', align: 'end', sortable: false }
    ];
  }

  get statusOptions(): SelectionOptions {
    return [
      { text: 'Pending', value: FEEDBACK_STATUSES.PENDING.label },
      { text: 'Resolved', value: FEEDBACK_STATUSES.RESOLVED.label },
      { text: 'Rejected', value: FEEDBACK_STATUSES.REJECTED.label }
    ];
  }

  get typeOptions(): SelectionOptions {
    return [
      { text: 'Give Feedback', value: FEEDBACK_TYPES.GIVE_FEEDBACK },
      { text: 'Suggest a feature', value: FEEDBACK_TYPES.SUGGEST_A_FEATURE },
      { text: 'Report a bug', value: FEEDBACK_TYPES.REPORT_A_BUG }
    ];
  }

  async mounted(): Promise<void> {
    const { data } = await fetchFeedbacks();
    this.feedbacks = data;
  }

  getDisplayStatus(status: string): DisplayStatus {
    return FEEDBACK_STATUSES[status];
  }

  filterByUser<T>(value: keyof T, search: string, item: FeedbackDTO): boolean {
    return (
      value !== null &&
      search !== null &&
      (item.user.fullName.toLowerCase().includes(search.toLowerCase()) || item.user.code.includes(search))
    );
  }

  onSearch(searchText: string): void {
    this.search = searchText;
  }

  onStatusChange(selectedStatuss: string): void {
    this.selectedStatuss = selectedStatuss;
  }

  onTypeChange(selectedTypes: string): void {
    this.selectedTypes = selectedTypes;
  }

  onPageChange(page: number): void {
    this.page = page;
  }

  onItemsPerPageChange(itemsPerPage: number): void {
    this.itemsPerPage = itemsPerPage;
  }

  onRejected(feedback: FeedbackDTO): void {
    this.selectedFeedback = Object.assign({}, feedback);
    this.dialogRejectShown = true;
  }

  async onResolved(feedback: FeedbackDTO): Promise<void> {
    const reqData: FeedbackDTO = {
      ...feedback,
      status: FEEDBACK_STATUSES.RESOLVED.label,
      userId: feedback.user.id
    };

    const { data } = await updateFeedbackStatus(reqData);
    const notiInfo = {
      showing: true,
      type: data ? NOTIFICATION.TYPE.success : NOTIFICATION.TYPE.error,
      title: data ? NOTIFICATION.TEXT.resolveFeedbackSuccess : NOTIFICATION.TEXT.resolveFeedbackFailed,
      content: [],
      icon: data ? NOTIFICATION.ICON.success : NOTIFICATION.ICON.error,
      multiline: false,
      height: 0
    };
    this.$store.commit('notification/setNotification', notiInfo);
    if (data) {
      const index = this.feedbacks.findIndex(f => f.id === data.id);
      Object.assign(this.feedbacks[index], data);
    }
  }

  onDialogSave(data: FeedbackDTO): void {
    const index = this.feedbacks.findIndex(f => f.id === data.id);
    Object.assign(this.feedbacks[index], data);
  }

  onImageClick(imgStr: string): void {
    this.selectedImage = imgStr;
    this.dialogImageShown = true;
  }

  onDialogImageClose(): void {
    this.selectedImage = '';
    this.dialogImageShown = false;
  }

  checkIsPendingStatus(status: string): boolean {
    return status === FEEDBACK_STATUSES.PENDING.label;
  }

  getDisplayType(type: string): string {
    const itemType = this.typeOptions.find(item => item.value === type);
    return itemType?.text || '';
  }

  getImageSource(imgStr: string): string {
    return this.imageUrl + imgStr;
  }
}
